import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { Image } from '@realadvisor/image';
import { ExternalLink, Home, MapPin } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { ActivityItemLotSnippetFragment } from '@graphql/__generated__/graphql';
import {
  snippetContainerSx,
  snippetIconColor,
  tooltipProps,
} from '@modules/_shared/components/feed-v2/constants/styles';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { getListingPrices } from '@modules/_shared/utils/propertyTemplateFunctions';
import { useLocale } from '@shared/hooks/locale';

export const ACTIVITY_ITEM_LOT_SNIPPET_FRAGMENT = gql(/* GraphQL */ `
  fragment LotSnippetFields on lots {
    id
    title
    created_at
    offer_type
    sale_price
    rent_net
    rent_extra
    currency
    price_unit
    hide_price_on_portals
    property {
      id
      route
      street_number
      postcode
      locality
      country_code
      computed_surface
      latest_appraisal {
        id
        value
        price_per_living_surface_meter_squared
      }
      property_images(order_by: { order_nr: asc }, limit: 1) {
        id
        image {
          id
          url
        }
      }
    }
  }

  fragment ActivityItemLotSnippet on activities {
    id
    lot_id
    buyer_lead_id
    enquiry_id
    lot {
      ...LotSnippetFields
    }
    buyer_lead {
      id
      lot {
        ...LotSnippetFields
      }
    }
    enquiry {
      id
      lot {
        ...LotSnippetFields
      }
    }
  }
`);

type ActivityItemLotSnippetProps = {
  activity: ActivityItemLotSnippetFragment;
};

type ResolvedLot = NonNullable<ActivityItemLotSnippetFragment['lot']>;

type ResolutionPath = 'buyer_lead' | 'enquiry' | 'direct';

const resolveLot = (
  activity: ActivityItemLotSnippetFragment,
): { lot: ResolvedLot; path: ResolutionPath } | null => {
  if (activity.buyer_lead?.lot != null) {
    return { lot: activity.buyer_lead.lot, path: 'buyer_lead' };
  }
  if (activity.enquiry?.lot != null) {
    return { lot: activity.enquiry.lot, path: 'enquiry' };
  }
  if (activity.lot != null) {
    return { lot: activity.lot, path: 'direct' };
  }
  return null;
};

const getLotLink = (
  activity: ActivityItemLotSnippetFragment,
  lotId: string,
  path: ResolutionPath,
): string => {
  if (path === 'buyer_lead' && activity.buyer_lead_id != null) {
    return `/listings/${lotId}/buyers/${activity.buyer_lead_id}`;
  }
  return `/listings/${lotId}/activity`;
};

export const ActivityItemLotSnippet = ({
  activity,
}: ActivityItemLotSnippetProps) => {
  const { t, locale } = useLocale();
  const theme = useTheme();

  const resolved = resolveLot(activity);
  if (resolved == null) {
    return null;
  }

  const { lot, path } = resolved;
  const lotLink = getLotLink(activity, lot.id, path);

  // The buyer-lead path links to the buyer lead, not the listing — keep the
  // tooltip precise about where the click lands.
  const tooltipTitle =
    path === 'buyer_lead'
      ? t('Click to view buyer lead')
      : t('Click to view listing');

  const imageUrl = lot.property?.property_images?.[0]?.image?.url ?? null;

  const { primary: price } = getListingPrices(lot, t, locale);
  const displayPrice = price ?? t('On request');

  const address = lot.property != null ? formatAddress(lot.property, ', ') : '';

  return (
    <Tooltip title={tooltipTitle} {...tooltipProps}>
      <Box
        component="a"
        href={lotLink}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          ...snippetContainerSx,
          display: 'block',
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          {/* Property thumbnail */}
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '6px',
              overflow: 'hidden',
              flexShrink: 0,
              bgcolor: 'grey.200',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {imageUrl != null ? (
              <Image src={imageUrl} srcSize={[[36, 36]]} />
            ) : (
              <Home
                size={16}
                style={{ color: (theme.vars ?? theme).palette.grey[400] }}
              />
            )}
          </Box>

          {/* Content */}
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            {/* Row 1: Title + enquiry/match counts + price */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack
                direction="row"
                spacing={0.75}
                sx={{
                  alignItems: 'center',
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <ExternalLink
                  size={12}
                  style={{
                    flexShrink: 0,
                    color: (theme.vars ?? theme).palette.grey[600],
                  }}
                />
                {lot.title != null && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {lot.title}
                  </Typography>
                )}
              </Stack>

              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  color: 'text.primary',
                  flexShrink: 0,
                }}
              >
                {displayPrice}
              </Typography>
            </Stack>

            {/* Row 2: Address + created_at */}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 0.25,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  alignItems: 'center',
                  minWidth: 0,
                }}
              >
                <MapPin
                  size={11}
                  color={snippetIconColor}
                  style={{ flexShrink: 0 }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {address || '-'}
                </Typography>
              </Stack>
              {lot.created_at != null && (
                <Typography
                  variant="caption"
                  component="span"
                  sx={{
                    color: 'text.secondary',
                    flexShrink: 0,
                  }}
                >
                  <TimeAgo dateString={lot.created_at} />
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Tooltip>
  );
};
